import { useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { fetchPlayers, createPlayer, updatePlayer, deletePlayer, linkPlayerToUser, fetchPlayer, fetchMyPlayer, lookupGhinHandicap, batchLookupGhinHandicaps, updatePlayerHandicap } from '../api/players'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { cacheQueryData, getCachedQueryData } from '../utils/scoreQueue'
import logger from '../utils/logger'

export function usePlayers() {
  return useQuery({
    queryKey: ['players'],
    queryFn: fetchPlayers,
  })
}

export function useCreatePlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createPlayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['players'] })
      queryClient.invalidateQueries({ queryKey: ['my-player'] })
      queryClient.invalidateQueries({ queryKey: ['my-follows'] })
      queryClient.invalidateQueries({ queryKey: ['my-followers'] })
    },
  })
}

export function useUpdatePlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...updates }) => updatePlayer(id, updates),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['players'] })
      queryClient.invalidateQueries({ queryKey: ['my-player'] })
      queryClient.invalidateQueries({ queryKey: ['player', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['matches'] })
      queryClient.invalidateQueries({ queryKey: ['match'] })
    },
  })
}

export function useDeletePlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deletePlayer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['players'] })
      queryClient.invalidateQueries({ queryKey: ['my-follows'] })
      queryClient.invalidateQueries({ queryKey: ['my-followers'] })
    },
  })
}

export function useLinkPlayer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: linkPlayerToUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['players'] })
    },
  })
}

export function usePlayer(playerId) {
  return useQuery({
    queryKey: ['player', playerId],
    queryFn: () => fetchPlayer(playerId),
    enabled: !!playerId,
  })
}

// Watchdog E: track whether profile data was previously populated
let lastMyPlayerHadName = false

export function useMyPlayer() {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: ['my-player', user?.id],
    queryFn: async () => {
      const data = await fetchMyPlayer()
      if (data && user?.id) cacheQueryData(`my-player_${user.id}`, data)
      return data
    },
    enabled: !!user,
    initialData: () => user?.id ? getCachedQueryData(`my-player_${user.id}`) : undefined,
    initialDataUpdatedAt: 0,
  })

  // Watchdog E: detect profile data wipe mid-session
  if (query.data?.first_name) {
    lastMyPlayerHadName = true
  } else if (lastMyPlayerHadName && query.data && !query.data.first_name) {
    logger.capture(new Error('Player profile data cleared mid-session'), {
      hadFirstName: true, currentData: !!query.data, isOnline: navigator.onLine, event: 'profile_wipe',
    })
    lastMyPlayerHadName = false // only report once
  }

  return query
}

export function useGhinLookup() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: lookupGhinHandicap,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-player'] })
      queryClient.invalidateQueries({ queryKey: ['players'] })
    },
    onError: (error, variables) => {
      logger.capture(error, { source: 'useGhinLookup', ghinNumber: variables?.ghin_number })
    },
  })
}

const STALE_HOURS = 24

function isStale(updatedAt) {
  if (!updatedAt) return true
  const ago = Date.now() - new Date(updatedAt).getTime()
  return ago > STALE_HOURS * 60 * 60 * 1000
}

/**
 * Auto-refresh the current user's handicap on app load if they have a
 * GHIN number and it hasn't been refreshed in 24+ hours.
 */
export function useAutoRefreshMyHandicap() {
  const { data: myPlayer } = useMyPlayer()
  const queryClient = useQueryClient()
  const ran = useRef(false)

  useEffect(() => {
    if (ran.current || !myPlayer) return
    if (!myPlayer.ghin_number || !isStale(myPlayer.handicap_updated_at)) return
    ran.current = true

    lookupGhinHandicap(myPlayer.ghin_number)
      .then((result) => {
        if (result.handicap_index != null) {
          updatePlayerHandicap(myPlayer.id, result.handicap_index).then(() => {
            queryClient.invalidateQueries({ queryKey: ['players'] })
            queryClient.invalidateQueries({ queryKey: ['my-player'] })
          })
        }
      })
      .catch((err) => {
        logger.warn('Auto GHIN refresh failed: ' + err.message)
      })
  }, [myPlayer])
}

/**
 * Batch refresh handicaps for all match players who have GHIN numbers.
 * Returns a mutation that accepts an array of player objects
 * (each with { id, ghin_number, handicap_updated_at }).
 */
export function useBatchRefreshHandicaps() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (players) => {
      // Manual button press — always refresh all players with GHIN numbers
      const ghinPlayers = players.filter((p) => p.ghin_number)
      if (ghinPlayers.length === 0) return { updated: 0 }

      const ghinNumbers = ghinPlayers.map((p) => p.ghin_number)
      const results = await batchLookupGhinHandicaps(ghinNumbers)

      // Update each player whose handicap was found
      const now = new Date().toISOString()
      let updated = 0
      for (const player of ghinPlayers) {
        const result = results[player.ghin_number]
        if (result && result.handicap_index != null) {
          // Update baseline player record via RPC (bypasses RLS for match owners)
          await updatePlayerHandicap(player.id, result.handicap_index)
          // Also update match_players record if provided
          if (player.match_player_id) {
            await supabase
              .from('match_players')
              .update({ handicap_index: result.handicap_index })
              .eq('id', player.match_player_id)
          }
          updated++
        }
      }

      return { updated }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['players'] })
      queryClient.invalidateQueries({ queryKey: ['my-player'] })
      queryClient.invalidateQueries({ queryKey: ['matches'] })
      queryClient.invalidateQueries({ queryKey: ['match'] })
    },
  })
}
